import React, { useState, useEffect } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Button, TextField, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Typography, Collapse, Divider, useMediaQuery, useTheme,
  Tooltip, CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Print from "@mui/icons-material/Print";
import Visibility from "@mui/icons-material/Visibility";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { QuotationListApi, QuotationDelete, getAllEnquiries ,getUnits, getDivisionUnits} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { generatePdf } from "./QuotationPrint";

let enquiryMapRef = {};
const setEnquiryMapData = (map) => { enquiryMapRef = map; };

const getEnqNo = (q) => {
  if (q.enquiry_no) return q.enquiry_no;
  if (q.enquiry && typeof q.enquiry === "object" && q.enquiry.enquiry_no) return q.enquiry.enquiry_no;
  if (q.enquiry && typeof q.enquiry === "number" && enquiryMapRef[q.enquiry]) return enquiryMapRef[q.enquiry];
  if (q.enquiry_id && enquiryMapRef[q.enquiry_id]) return enquiryMapRef[q.enquiry_id];
  if (q.enquiry_details?.enquiry_no) return q.enquiry_details.enquiry_no;
  return "-";
};
const getEnqId = (q) => q.enquiry_id || q.enquiry?.id || q.enquiry?.enquiry_id || q.enquiry_details?.id || null;
const getName = (q) => q.to_name || q.contact_name || q.party_name || "-";
const getComp = (q) => q.to_company || q.company_name || "-";
const getDate = (q) => q.date || q.created_at?.split("T")[0] || "-";
const trunc = (t, l = 18) => (!t ? "" : t.length > l ? t.substring(0, l) + "…" : t);

const QuotationTable = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delOpen, setDelOpen] = useState(false);
  const [delQuote, setDelQuote] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [selQuote, setSelQuote] = useState(null);
  const [s1, setS1] = useState("");
  const [s2, setS2] = useState("");
  const [units, setUnits] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const nav = useNavigate();
  const theme = useTheme();
  const mob = useMediaQuery(theme.breakpoints.down("md"));

  const fetch = async () => {
    setLoading(true);
    try {
      const [res, enq, unitRes] = await Promise.all([
        QuotationListApi(), 
        getAllEnquiries(),
        getDivisionUnits().catch(() => getUnits())
      ]);
      setQuotations(Array.isArray(res) ? res : res?.results || []);
      
      const arr = Array.isArray(enq) ? enq : enq?.results || [];
      const m = {};
      arr.forEach((e) => { m[e.enquiry_id || e.id] = e.enquiry_no; });
      setEnquiryMapData(m);
      
      // ✅ Units list save
      const uList = unitRes?.units || unitRes?.results || unitRes || [];
      setUnits(Array.isArray(uList) ? uList : []);
    } catch { toast.error("Failed to load"); setQuotations([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);
  useEffect(() => { document.body.style.overflow = mob ? "auto" : "hidden"; return () => { document.body.style.overflow = "auto"; }; }, [mob]);

  const confirmDel = async () => {
    if (!delQuote?.quotation_id) { toast.error("Invalid"); return; }
    try { await QuotationDelete(delQuote.quotation_id); setQuotations(p => p.filter(q => q.quotation_id !== delQuote.quotation_id)); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
    setDelOpen(false); setDelQuote(null);
  };

  const editNav = (q) => {
    const eno = getEnqNo(q), eid = getEnqId(q);
    nav("/dashboard/quotationForm", {
      state: {
        data: { ...q, enquiry_no: eno !== "-" ? eno : q.enquiry_no || "", enquiry_id: eid },
        selectedEnquiry: eno !== "-" ? { enquiry_no: eno, id: eid, ...(q.enquiry || {}) } : null,
        isEdit: true,
      },
    });
  };

  const filtered = quotations.filter((q) => {
    const en = getEnqNo(q);
    const c1 = (q.to_name || "").toLowerCase().includes(s1.toLowerCase()) || (q.ref_no || "").toLowerCase().includes(s1.toLowerCase()) || (en || "").toLowerCase().includes(s1.toLowerCase()) || (q.contact_no || "").includes(s1);
    const c2 = (q.to_company || "").toLowerCase().includes(s2.toLowerCase());
    return c1 && c2;
  });

  const tot = filtered.reduce((s, q) => s + (parseFloat(q.total_amount) || 0), 0);

  // ── Compact row style ──
  const rs = { "& td": { fontSize: "0.72rem", py: 0.4, px: 0.8 } };
  const hs = { fontWeight: 600, fontSize: "0.73rem", py: 0.6, px: 0.8, bgcolor: "#f0f0f0", color: "#333", borderBottom: "1px solid #ccc" };

  return (
    <Box sx={{ px: 0, pt: 0, mt: { xs: 3, md: -3 }, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={2500} />

      {/* Search */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField fullWidth label="Search Ref / Enq / Party / Phone" size="small" value={s1} onChange={e => setS1(e.target.value)} sx={{ "& .MuiInputBase-root": { height: 32 } }} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth label="Search Company" size="small" value={s2} onChange={e => setS2(e.target.value)} sx={{ "& .MuiInputBase-root": { height: 32 } }} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" onClick={() => nav("/dashboard/quotationForm")} startIcon={<AddIcon />} sx={{ height: 32, fontSize: "0.75rem", bgcolor: "#0ba1f2", "&:hover": { bgcolor: "#555" } }}>
              Quotation
            </Button>
          </Grid>
        </Grid>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>
      ) : mob ? (
        /* ── Mobile ── */
        <Box sx={{ bgcolor: "#fff" }}>
          {filtered.length === 0 && <Typography sx={{ p: 2, textAlign: "center", color: "#999", fontSize: "0.8rem" }}>No Quotation Found</Typography>}
          {filtered.map((q, i) => (
            <Box key={q.quotation_id || i}>
              <Box onClick={() => setExpanded(expanded === q.quotation_id ? null : q.quotation_id)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1, cursor: "pointer", bgcolor: "#fff", borderBottom: "1px solid #eee" }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.3 }}>
                    <Typography sx={{ fontSize: "0.65rem", color: "#999", minWidth: 20 }}>#{i + 1}</Typography>
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>{q.ref_no || "-"}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, flex: 1 }}>{q.to_title ? `${q.to_title} ` : ""}{getName(q)}</Typography>
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>₹{Number(q.total_amount || 0).toLocaleString("en-IN")}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.65rem", color: "#888" }}>{trunc(getComp(q), 25)} • {q.contact_no || "-"}</Typography>
                </Box>
                <IconButton size="small" sx={{ p: 0.3 }}>{expanded === q.quotation_id ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}</IconButton>
              </Box>
              <Collapse in={expanded === q.quotation_id} unmountOnExit>
                <Box sx={{ px: 1.5, py: 0.8, bgcolor: "#fafafa", borderBottom: "1px solid #eee" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mb: 0.8 }}>
                    {[
                      ["REF", q.ref_no], ["ENQ", getEnqNo(q)], ["EMAIL", q.email], ["CONTACT", q.contact_no],
                      ["DATE", getDate(q) !== "-" ? new Date(getDate(q)).toLocaleDateString("en-GB") : "-"],
                      ["AMOUNT", `₹ ${Number(q.total_amount || 0).toLocaleString("en-IN")}`]
                    ].map(([l, v]) => (
                      <Box key={l}>
                        <Typography sx={{ fontSize: "0.6rem", color: "#aaa", letterSpacing: 0.5 }}>{l}</Typography>
                        <Typography sx={{ fontSize: "0.68rem", fontWeight: 500 }}>{v || "-"}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                    {[
                      { icon: <Visibility sx={{ fontSize: 14 }} />, color: "#1976d2", fn: () => { setSelQuote(q); setViewOpen(true); } },
                      { icon: <EditIcon sx={{ fontSize: 14 }} />, color: "#1976d2", fn: () => editNav(q) },
                      { icon: <DeleteIcon sx={{ fontSize: 14 }} />, color: "#d32f2f", fn: () => { setDelQuote(q); setDelOpen(true); } },
                      { icon: <Print sx={{ fontSize: 14 }} />, color: "#1976d2", fn: () => generatePdf(q) },
                    ].map((a, j) => (
                      <IconButton key={j} size="small" onClick={e => { e.stopPropagation(); a.fn(); }} sx={{ width: 26, height: 26, border: "1px solid #ddd", color: a.color }}>{a.icon}</IconButton>
                    ))}
                  </Box>
                </Box>
              </Collapse>
            </Box>
          ))}
          {filtered.length > 0 && (
            <Box sx={{ px: 1.5, py: 1, borderTop: "1px solid #ccc", display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#888" }}>Total: <b>{filtered.length}</b></Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>₹ {tot.toLocaleString("en-IN")}</Typography>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop ── */
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "88vh", overflow: "auto", border: "1px solid #ddd" }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1400, "& td, & th": { textAlign: "center", whiteSpace: "nowrap", borderBottom: "1px solid #e0e0e0" } }}>
            <TableHead>
              <TableRow>
                {["S.No.", "Ref No", "Enq No", "Party Name", "Company", "Contact", "Email", "Subject", "Amount", "Date", "Action"].map((h, i) => (
                  <TableCell key={h} sx={{
                    ...hs,
                    ...(i === 0 ? { position: "sticky", left: 0, zIndex: 3 } : {}),
                    ...(i === 1 ? { position: "sticky", left: 45, zIndex: 3 } : {}),
                    ...(i === 10 ? { position: "sticky", right: 0, zIndex: 3 } : {}),
                  }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} sx={{ py: 2, color: "#999", fontSize: "0.75rem" }}>No Data Found</TableCell></TableRow>
              ) : filtered.map((q, i) => (
                <TableRow key={q.quotation_id || i} hover sx={{ ...rs, bgcolor: "#fff", "&:hover": { bgcolor: "#f9f9f9" } }}>
                  <TableCell sx={{ position: "sticky", left: 0, bgcolor: "#fff", zIndex: 1 }}>{i + 1}</TableCell>
                  <TableCell sx={{ position: "sticky", left: 45, bgcolor: "#fff", zIndex: 1, fontWeight: 600 }}>{q.ref_no || "-"}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{getEnqNo(q)}</TableCell>
                  <TableCell>{q.to_title ? `${q.to_title} ` : ""}{getName(q)}</TableCell>
                  <TableCell><Tooltip title={getComp(q)}><span>{trunc(getComp(q))}</span></Tooltip></TableCell>
                  <TableCell>{q.contact_no || "-"}</TableCell>
                  <TableCell><Tooltip title={q.email || ""}><span>{trunc(q.email)}</span></Tooltip></TableCell>
                  <TableCell><Tooltip title={q.subject || ""}><span>{trunc(q.subject, 22)}</span></Tooltip></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>₹{Number(q.total_amount || 0).toLocaleString("en-IN")}</TableCell>
                  <TableCell>{q.date}</TableCell>
                  <TableCell sx={{ position: "sticky", right: 0, bgcolor: "#fff", zIndex: 1 }}>
                    <IconButton size="small" onClick={() => { setSelQuote(q); setViewOpen(true); }} sx={{ p: 0.3, color: "#1976d2" }} title="View"><Visibility sx={{ fontSize: 15 }} /></IconButton>
                    <IconButton size="small" onClick={() => editNav(q)} sx={{ p: 0.3, color: "#1976d2" }} title="Edit"><EditIcon sx={{ fontSize: 15 }} /></IconButton>
                    <IconButton size="small" onClick={() => { setDelQuote(q); setDelOpen(true); }} sx={{ p: 0.3, color: "#d32f2f" }} title="Delete"><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
                    <IconButton size="small" onClick={() => generatePdf(q, units)} sx={{ p: 0.3, color: "#1976d2" }} title="Print"><Print sx={{ fontSize: 15 }} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtered.length > 0 && (
            <Box sx={{ px: 2, py: 0.6, borderTop: "1px solid #ccc", display: "flex", justifyContent: "space-between", bgcolor: "#f8f8f8", position: "sticky", bottom: 0 }}>
              <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>Total: <b>{filtered.length}</b></Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>Grand Total: ₹ {tot.toLocaleString("en-IN")}</Typography>
            </Box>
          )}
        </TableContainer>
      )}

      {/* Delete Dialog */}
      <Dialog open={delOpen} onClose={() => setDelOpen(false)} PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontSize: "0.9rem", py: 1.5 }}>Confirm Delete</DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography sx={{ fontSize: "0.8rem" }}>Delete <b>{delQuote?.ref_no}</b> ({getName(delQuote || {})})?</Typography>
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button onClick={() => setDelOpen(false)} size="small" sx={{ color: "#333" }}>Cancel</Button>
          <Button variant="contained" size="small" onClick={confirmDel} sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#b71c1c" } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewOpen} onClose={() => { setViewOpen(false); setSelQuote(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        {selQuote && (
          <>
            <DialogTitle sx={{ py: 1.2, borderBottom: "1px solid #ddd", fontSize: "0.9rem", fontWeight: 600 }}>
              Quotation — {selQuote.ref_no}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 2 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.2, mb: 2, p: 1.5, border: "1px solid #eee", borderRadius: 1 }}>
                {[
                  ["Ref No", selQuote.ref_no], ["Enquiry No", getEnqNo(selQuote)],
                  ["Party", `${selQuote.to_title || ""} ${getName(selQuote)}`], ["Company", getComp(selQuote)],
                  ["Date", getDate(selQuote)], ["Contact", selQuote.contact_no],
                  ["Email", selQuote.email], ["Subject", selQuote.subject],
                ].map(([l, v]) => (
                  <Box key={l}>
                    <Typography sx={{ fontSize: "0.65rem", color: "#999", textTransform: "uppercase", mb: 0.2 }}>{l}</Typography>
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>{v || "-"}</Typography>
                  </Box>
                ))}
              </Box>

              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.8 }}>Items ({(selQuote.items_list || []).length})</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["#", "Category", "Item", "Unit", "Description", "Qty", "Rate", "Amount"].map(h => (
                        <TableCell key={h} align="center" sx={{ fontWeight: 600, fontSize: "0.7rem", py: 0.5, bgcolor: "#f5f5f5" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selQuote.items_list || []).map((it, i) => (
                      <TableRow key={i}>
                        <TableCell align="center" sx={{ fontSize: "0.7rem", py: 0.3 }}>{i + 1}</TableCell>
                        <TableCell sx={{ fontSize: "0.7rem", py: 0.3 }}>{it.category_name || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.7rem", py: 0.3, fontWeight: 500 }}>{it.item_name || "-"}</TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.7rem", py: 0.3 }}>{it.unit_name || it.unit || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.7rem", py: 0.3, color: "#666", maxWidth: 140, whiteSpace: "normal" }}>{it.description || "-"}</TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.7rem", py: 0.3 }}>{it.quantity}</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.7rem", py: 0.3 }}>₹{Number(it.rate || 0).toLocaleString("en-IN")}</TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.7rem", py: 0.3, fontWeight: 500 }}>₹{Number(it.amount || 0).toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  Grand Total: ₹ {Number(selQuote.total_amount || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ py: 1, px: 2, borderTop: "1px solid #eee" }}>
              <Button onClick={() => generatePdf(selQuote)} startIcon={<Print />} size="small" variant="outlined" sx={{ borderColor: "#ccc", color: "#2e7d32" }}>Print</Button>
              <Button onClick={() => { setViewOpen(false); setSelQuote(null); }} size="small" variant="contained" sx={{ bgcolor: "#333", "&:hover": { bgcolor: "#555" } }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default QuotationTable;